import React, { useContext, useState } from 'react';
import { createTheme, ThemeProvider, responsiveFontSizes } from '@mui/material/styles';
import { 
    CssBaseline, 
    Box, 
    Typography, 
    Button, 
    TextField, 
    Paper, 
    Container,
    Avatar,
    InputAdornment
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import {monitorTheme as theme} from '../theme'
import { InfoContext as SPInfoContext } from '../context/InfoContext';
import {clientsApi} from '../apiUser'; // Adjust the import path as necessary
import { useNavigate } from 'react-router-dom';
import WaitingDialog from '../components/WaitingDialog'; // Adjust the import path as necessary
import { set } from 'date-fns';

// --- LOGIN PAGE COMPONENT ---
const LoginPage = (props) => {
  const [id, setId] = useState('');
  const [pin, setPin] = useState('');
  const [idError, setIdError] = useState('');
  const [pinError, setPinError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const {info, setInfo} = useContext(SPInfoContext);
  const navigate = useNavigate();

  const handleIdChange = (e) => {
    setIdError('');
    const value = e.target.value.replace(/[^0-9]/g, ''); // Only allow numbers
    if (value.length <= 3) setId(value);
  };

  const handlePinChange = (e) => {
    setPinError('');
    const value = e.target.value.replace(/[^0-9]/g, ''); // Only allow numbers
    if (value.length <= 5) setPin(value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setIdError('');
    setPinError('');

    let valid = true;
    if (id.length !== 3 || !/^\d{3}$/.test(id)) {
      setIdError('ID must be 3 digits.');
      valid = false;
    }
    if (pin.length !== 5 || !/^\d{5}$/.test(pin)) {
      setPinError('PIN must be 5 digits.');
      valid = false;
    }

    if (!valid) {
      setOpenDialog(false);
      return;
    }

    setOpenDialog(true);

    try {
      if (id === "000" && pin === "00001") {
        const response = await clientsApi.get();
        setInfo((prevInfo) => ({
          ...prevInfo,
          utenti: response.data,
        }));
        console.log('Client Info:', response.data);
        props.onAdmin(true); // Call the onAdmin function to set admin state
        navigate('/sp/devices'); // Redirect to the SP dashboard
        return;
      }

      const response = await clientsApi.login({
        client_id: id,
        password: pin,
      });

      if (!response.data || !response.data.jwt) {
        alert('Login failed: Invalid response from server.');
        setOpenDialog(false);
        return;
      }

      console.log('Login successful:', response.data);
      localStorage.setItem('accessToken', response.data.jwt);

      setInfo((prevInfo) => ({
        ...prevInfo,
        selectedClient: response.data,
      }));

      setTimeout(() => {
        navigate('/dsmm/home'); // Redirect to the SP dashboard
      }, 4000);
    } catch (error) {
      console.error('Error during login:', error);
      setPinError('Password o ID errati.');
    } finally {
      setOpenDialog(false);
    }

    console.log('Login Submitted', { id, pin });
  };

  return (
    <>
    <WaitingDialog open={openDialog} onClose={() => setOpenDialog(false)} message="Logging in..."/>
    
    <Container component="main" maxWidth="xs" sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      <Paper 
        sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: { xs: 3, sm: 5 }, // Responsive padding
            width: '100%',
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: 'primary.main', width: 56, height: 56 }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5" sx={{ mb: 1 }}>
          DSMM24
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Registrati con il tuo ID e PIN per accedere al sistema.
        </Typography>
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="id"
            label="3-Digit ID"
            name="id"
            autoComplete="off"
            autoFocus
            type="text" // Use text to better control input
            value={id}
            onChange={handleIdChange}
            error={!!idError}
            helperText={idError}
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        <PersonOutlineIcon sx={{color: 'text.secondary'}}/>
                    </InputAdornment>
                )
            }}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="pin"
            label="5-Digit PIN"
            type="password"
            id="pin"
            value={pin}
            onChange={handlePinChange}
            error={!!pinError}
            helperText={pinError}
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        <VpnKeyOutlinedIcon sx={{color: 'text.secondary'}}/>
                    </InputAdornment>
                )
            }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
          >
            Sign In
          </Button>
        </Box>
      </Paper>
    </Container>
    </>
  );
};

// --- APP WRAPPER ---
export function Login(props) {
  return (
      <Box 
        sx={{ 
          minHeight: '100vh', 
          // A more subtle and modern background
          background: `
            radial-gradient(circle at 1% 1%, ${theme.palette.primary.main}1A, transparent 25%),
            radial-gradient(circle at 99% 99%, ${theme.palette.secondary.main}1A, transparent 25%),
            ${theme.palette.background.default}
          `,
          backgroundSize: 'cover',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >

        <LoginPage onAdmin={props.onAdmin}/>
      </Box>
  );
}
